import { SetStateAction, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";
import {
  getAuctionEventById,
  getBidderByUuid,
  addBidderForEvent,
  updateBidderForEvent,
  getTeamMembers,
  getMultipleFilesByOwnerIdentifier,
  bulkFileOwnerServicechange,
} from "services";
import { DateTime } from "luxon";
import { Input } from "../../../shadcn/components/ui/input";
import { BetaForm as useBetaForm } from "@reusejs/react";
import { Switch } from "../../../shadcn/components/ui/switch";
import { Button } from "../../../shadcn/components/ui/button";
import { Checkbox } from "../../../shadcn/components/ui/checkbox";

import CustomMultiplePDFViewer from "./CustomMultiplePDFViewer";
import CustomMultipleFileUploader from "./CustomMultipleFileUploader";
import CheckIcon from "../../../icons/CheckIcon";
import { v4 as uuidv4 } from "uuid";
import CustomRejectionModal from "./CustomRejectionModal";
import { Dropdown } from "../../../shadcn/components/custom/Dropdown";
import { fetchIDCardDetails } from "services/bidder";
import createJointBidderPayload from "./JointBidder/createJointBidderPayload";
import CustomErrorHandlingModal from "./CustomErrorHandlingModal";

const isDigioPanFullVerificationEnabled =
  process.env.NEXT_PUBLIC_DIGIO_PAN_FULL_VERIFICATION_ENABLED === "true";

interface BidderFormProps {
  mode?: string;
  allBiddersData?: any;
  currentBidderData?: any;
  activeStep?: number;
  setActiveStep?: React.Dispatch<SetStateAction<number>>;
  bidderFormWrapperClassName?: string;
  setCurrentBidderData?: React.Dispatch<SetStateAction<Array<any>>>;
  totalBidders?: number;
  primaryBidderIndex?: number;
  setPrimaryBidderIndex?: React.Dispatch<SetStateAction<number>>;
}
export const BidderForm = ({
  mode,
  currentBidderData,
  allBiddersData,
  activeStep = 0,
  setActiveStep,
  bidderFormWrapperClassName,
  setCurrentBidderData,
  totalBidders,
  primaryBidderIndex,
  setPrimaryBidderIndex,
}: BidderFormProps) => {
  const router = useRouter();
  const { for_event, bidders_count } = router.query;
  const { id } = router.query;
  const [event, setEvent] = useState<any>([]);
  const [renderKey, setRenderKey] = useState(0);
  const [bidderData, setBidderData] = useState<any>();
  const [membersEmail, setMembersEmail] = useState<any>([]);
  const [kycFiles, setKycFiles] = useState<any>([]);
  const [jointDeclarationFiles, setJointDeclarationFiles] = useState<any>([]);
  const [jointDeclarationRandomId, setJointDeclarationRandomId] =
    useState<any>();
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [randomId, setRandomId] = useState<any>();
  const [modalType, setModalType] = useState("");
  const [isFetchingPan, setIsFetchingPan] = useState(false);
  const [isNameVerified, setIsNameVerified] = useState(false);
  type ErrorType = "validation" | "api" | "error";
  const [isErrorHandlingModalOpen, setIsErrorHandlingModalOpen] = useState<{
    open: boolean;
    type: ErrorType;
  }>({
    open: false,
    type: "validation",
  });
  const [errorHandlingModalData, setErrorHandlingModalData] =
    useState<any>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isOverwrite, setIsOverwrite] = useState(false);

  const banker = useSelector((state: any) => {
    return state.bankerAuth.user;
  });

  const currentBranch = useSelector((state: any) => {
    return state.bankerAuth.currentBranch;
  });

  const bank = useSelector((state: any) => {
    return state.bankerAuth.bank;
  });

  const getTeamMembersEmail = async () => {
    let response: any = await getTeamMembers(currentBranch?.team["teams*uuid"]);
    const filteredData = response.data
      .map((item: any) => {
        const emailAttr = item?.attributes?.find(
          (attr: any) => attr.type === "email",
        );
        return emailAttr ? emailAttr.value : null;
      })
      .filter(Boolean);
    setMembersEmail(filteredData);
  };

  useEffect(() => {
    if ((id && mode === "view") || mode === "edit") {
      (async () => {
        await bidderinit(id);
      })();
    }
    if (for_event) {
      (async () => {
        await init(for_event);
      })();
    }
    getTeamMembersEmail();
  }, [for_event, id]);

  useEffect(() => {
    if (mode === "create" || mode === "joint-creation") setRandomId(uuidv4());
    if (mode === "joint-creation") setJointDeclarationRandomId(uuidv4());
  }, []);

  useEffect(() => {
    if (currentBidderData && mode === "joint-creation") {
      setBidderData(currentBidderData);
    }
  }, [activeStep, currentBidderData]);

  const init = async (id: any) => {
    const data: any = await getAuctionEventById(id);
    setEvent(data);
  };

  const bidderinit = async (id: any) => {
    const data: any = await getBidderByUuid(id);
    setBidderData(data);

    if (data) {
      const kycData: any = await getMultipleFilesByOwnerIdentifier(
        id,
        data?.created_by === "banker"
          ? "kyc-documents"
          : "signed-bid-request-agreement",
      );

      if (kycData) {
        const filteredKycdata = kycData?.map((item: any) => ({
          id: item?.uuid,
          mime: item?.mime,
          filename: item?.file_name,
        }));
        setKycFiles(filteredKycdata);
        setRenderKey(renderKey + 1);
      }

      if (data?.is_joint && data?.access_type === "primary") {
        const jointDeclarationData: any =
          await getMultipleFilesByOwnerIdentifier(
            id,
            "joint-declaration-documents",
          );

        if (jointDeclarationData) {
          const filteredJointDeclarationData = jointDeclarationData?.map(
            (item: any) => ({
              id: item?.uuid,
              mime: item?.mime,
              filename: item?.file_name,
            }),
          );
          setJointDeclarationFiles(filteredJointDeclarationData);
          setRenderKey((prev) => prev + 1);
        }
      }
    }
  };

  const mobileNumberRegEx: RegExp = /^[0-9]{10}$/;
  const panRegex: RegExp = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  const aadharRegex: RegExp = /^(?:[2-9][0-9]{11})?$/;

  useEffect(() => {
    if (!bidderData) return;

    if (mode === "joint-creation") {
      setIsNameVerified(bidderData?.name ? true : false);
    }
    form.setField("email", bidderData?.email || "");
    form.setField(
      "name",
      mode === "joint-creation"
        ? bidderData?.name
        : bidderData?.meta?.name || "",
    );
    form.setField(
      "pan",
      mode === "joint-creation" ? bidderData?.pan : bidderData?.meta?.pan || "",
    );
    form.setField("kyc_verified", bidderData?.kyc_verified ?? false);
    form.setField("emd_amount_paid", bidderData?.emd_amount_paid ?? false);
    form.setField("agreement_signed", bidderData?.agreement_signed ?? false);
    form.setField(
      "application_form_filled",
      bidderData?.application_form_filled ?? false,
    );
    const userDetails =
      mode === "joint-creation"
        ? bidderData
        : bidderData?.meta?.user_details || {};
    form.setField("aadhar_number", userDetails.aadhar_number || "");
    form.setField("mobile_number", userDetails.mobile_number || "");
    form.setField("address", userDetails.address || "");
    form.setField("user_type", userDetails?.user_type || "");
    if (
      bidderData?.errors &&
      bidderData?.errors?.email &&
      bidderData?.errors?.mobile_number
    ) {
      form.setErrors({
        email: [bidderData?.errors?.email],
        mobile_number: [bidderData?.errors?.mobile_number],
      });
    }
  }, [bidderData]);

  const form = useBetaForm({
    email: bidderData ? bidderData.email : "",
    name: bidderData
      ? bidderData?.meta?.name
        ? bidderData?.meta?.name
        : ""
      : "",

    pan: bidderData ? (bidderData?.meta?.pan ? bidderData?.meta?.pan : "") : "",
    kyc_verified: bidderData ? bidderData.kyc_verified : false,
    emd_amount_paid: bidderData ? bidderData.emd_amount_paid : false,
    agreement_signed: bidderData ? bidderData.agreement_signed : false,
    application_form_filled: bidderData
      ? bidderData.application_form_filled
      : false,
    aadhar_number: bidderData
      ? bidderData?.meta?.user_details?.aadhar_number
        ? bidderData?.meta?.user_details?.aadhar_number
        : ""
      : "",
    mobile_number: bidderData
      ? bidderData?.meta?.user_details?.mobile_number
        ? bidderData?.meta?.user_details?.mobile_number
        : ""
      : "",
    address: bidderData
      ? bidderData?.meta?.user_details?.address
        ? bidderData?.meta?.user_details?.address
        : ""
      : "",
    user_type: bidderData
      ? bidderData?.meta?.user_details?.user_type
        ? bidderData?.meta?.user_details?.user_type
        : ""
      : "",
  });

  const form_constraints = {
    email: {
      presence: {
        allowEmpty: false,
      },
      email: true,
      exclusion: membersEmail,
    },
    name: {
      presence: {
        allowEmpty: false,
      },
    },
    pan: {
      presence: {
        allowEmpty: false,
      },
      format: {
        pattern: panRegex,
        message: "^PAN number is not valid.",
      },
    },
    aadhar_number: {
      presence: {
        allowEmpty: true,
      },
      format: {
        pattern: aadharRegex,
        message: "^Aadhar number is not valid.",
      },
    },
    mobile_number: {
      presence: {
        allowEmpty: false,
      },
      format: {
        pattern: mobileNumberRegEx,
        message: "^Mobile number is not valid.",
      },
    },
    address: {
      presence: {
        allowEmpty: true,
      },
    },
    user_type: {
      presence: {
        allowEmpty: false,
      },
    },

    // status: {
    //   presence: {
    //     allowEmpty: false,
    //   },
    // },
  };

  useEffect(() => {
    form.setValidationRules(form_constraints);
  }, [membersEmail]);

  const handleAcceptEvent = async () => {
    try {
      const data: any = await updateBidderForEvent({
        uuid: id,
        status: "verified",
        kyc_verified: true,
        emd_amount_paid: true,
        agreement_signed: true,
        application_form_filled: true,
      });
      if (data) router.back();
    } catch (err) {
      console.log("error", err);
    }
  };

  const handleRejectEvent = async (cancellationReason: string) => {
    try {
      if (id === "") return;
      setIsLoading(true);
      const data: any = await updateBidderForEvent({
        uuid: id,
        status: "rejected",
        remarks: cancellationReason,
      });
      if (data) {
        router.reload();
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      console.log("check here error", error);
    }
  };

  const handleVerifyPan = async (panData: any) => {
    if (panData) {
      const userEnteredName = (form.getField("name") || "").toString().trim();

      const isPanStatusValid = isDigioPanFullVerificationEnabled
        ? panData?.status?.toLowerCase() === "valid"
        : panData?.status === "VALID";

      const isNameLongEnough = userEnteredName.length >= 3;
      const isNameMatch = isDigioPanFullVerificationEnabled
        ? isNameLongEnough && panData?.name_as_per_pan_match === true
        : true;

      if (
        panData?.category?.toLowerCase() !==
        form.getField("user_type")?.value?.toLowerCase()
      ) {
        form.pushError({
          pan: ["Please enter valid PAN based on bidder type"],
        });
        if (!isDigioPanFullVerificationEnabled) {
          form.setField("name", "");
        }
        setIsNameVerified(false);
      } else if (!isPanStatusValid) {
        form.pushError({
          pan: ["PAN seems invalid. Please check the number and try again."],
        });
        if (!isDigioPanFullVerificationEnabled) {
          form.setField("name", "");
        }
        setIsNameVerified(false);
      } else if (!isNameMatch) {
        if (!isNameLongEnough) {
          form.pushError({
            pan: [
              "Please enter name (minimum 3 characters) before PAN verification.",
            ],
          });
        } else {
          form.pushError({
            pan: [
              "The name doesn't match PAN records. Please enter name exactly as on PAN card.",
            ],
          });
        }
        setIsNameVerified(false);
      } else {
        form.pushError({ pan: [] });
        if (!isDigioPanFullVerificationEnabled) {
          form.setField("name", panData?.full_name);
        }
        setIsNameVerified(true);
      }
    } else {
      form.setField("name", "");
      form.setField("pan", "");
      form.pushError({ pan: ["Please enter pan number"] });
      setIsNameVerified(false);
    }
  };

  const switchFields = [
    { title: "KYC Verified", value: "kyc_verified" },
    { title: "EMD Amount Paid", value: "emd_amount_paid" },
    { title: "Agreement Signed", value: "agreement_signed" },
    { title: "Application Form Filled", value: "application_form_filled" },
  ];

  const handleSwitchChange = (checked: boolean, form_field: string) => {
    form.setField(form_field, checked);
  };

  const saveCurrentBidderData = () => {
    if (
      setCurrentBidderData &&
      activeStep !== undefined &&
      activeStep >= 0 &&
      activeStep < Number(bidders_count || 0)
    ) {
      const currentFormData = {
        name: form.getField("name"),
        email: form.getField("email"),
        pan: form.getField("pan"),
        mobile_number: form.getField("mobile_number"),
        aadhar_number: form.getField("aadhar_number"),
        address: form.getField("address"),
        user_type: form.getField("user_type"),
        kyc_verified: form.getField("kyc_verified"),
        emd_amount_paid: form.getField("emd_amount_paid"),
        agreement_signed: form.getField("agreement_signed"),
        application_form_filled: form.getField("application_form_filled"),
      };

      setCurrentBidderData((prevData: any) => {
        const updatedData = [...(prevData || [])];
        updatedData[activeStep] = currentFormData;
        return updatedData;
      });
    }
  };

  const handleValidationForJointParticipation = async () => {
    if (form.validate() && setActiveStep && (activeStep === 0 || activeStep)) {
      // Save current bidder data before moving to next step
      saveCurrentBidderData();
      setIsNameVerified(false);
      setActiveStep(activeStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (setActiveStep && activeStep && activeStep > 0) {
      // Save current bidder data before going back
      setActiveStep(activeStep - 1);
      saveCurrentBidderData();
    }
  };

  const validateBidders = (allBiddersData: any): boolean => {
    let isValid = true;

    const emailIdx = new Map<string, number>();
    const mobileIdx = new Map<string, number>();
    const panIdx = new Map<string, number>();

    allBiddersData.forEach((b: any, idx: number) => {
      b.errors = {};
      if (!b.email) {
        b.errors.email = "Bidder Email is required";
        isValid = false;
      }
      if (!b.mobile_number) {
        b.errors.mobile_number = "Bidder Mobile number is required";
        isValid = false;
      }
      if (!b.pan) {
        b.errors.pan = "Bidder PAN is required";
        isValid = false;
      }

      if (b.email) {
        if (emailIdx.has(b.email)) {
          b.errors.email = "Email must be unique";
          allBiddersData[emailIdx.get(b.email)!].errors!.email =
            "Email must be unique";
          isValid = false;
        } else {
          emailIdx.set(b.email, idx);
        }
      }

      if (b.mobile_number) {
        if (mobileIdx.has(b.mobile_number)) {
          b.errors.mobile_number = "Mobile number must be unique";
          allBiddersData[
            mobileIdx.get(b.mobile_number)!
          ].errors!.mobile_number = "Mobile number must be unique";
          isValid = false;
        } else {
          mobileIdx.set(b.mobile_number, idx);
        }
      }

      // if (b.pan) {
      //   if (panIdx.has(b.pan)) {
      //     b.errors.pan = "PAN must be unique";
      //     allBiddersData[panIdx.get(b.pan)!].errors!.pan = "PAN must be unique";
      //     isValid = false;
      //   } else {
      //     panIdx.set(b.pan, idx);
      //   }
      // }
    });

    if (!isValid) {
      const allErrors = allBiddersData.map((b: any, idx: number) => ({
        index: idx,
        ...b.errors,
      }));
      // console.log("allErrors: ", allErrors);
      setErrorHandlingModalData(allErrors);

      // setIsErrorHandlingModalOpen({ open: true, type: "validation" });
    }

    return isValid;
  };

  const handleSubmitAllBidders = async () => {
    try {
      if (setPrimaryBidderIndex) setPrimaryBidderIndex(0);
      if (mode === "joint-creation") {
        try {
          const eventData: any = await getAuctionEventById(for_event);
          if (!validateBidders(allBiddersData) && setActiveStep) {
            console.log("Invalid bidders data", allBiddersData);
            setIsErrorHandlingModalOpen({ open: true, type: "validation" });
            setActiveStep(0);
            return;
          }
          const payload = await createJointBidderPayload(
            eventData,
            allBiddersData,
            banker,
            bank,
            currentBranch,
            primaryBidderIndex,
            isOverwrite,
          );
          console.log("payload: ", payload);
          const res: any = await addBidderForEvent(payload);

          if (!res?.uuid) {
            router.back();
            return;
          }
          await Promise.all([
            kycFiles?.length
              ? bulkFileOwnerServicechange({
                  older_id: randomId,
                  newer_id: res.uuid,
                })
              : null,
            jointDeclarationFiles?.length
              ? bulkFileOwnerServicechange({
                  older_id: jointDeclarationRandomId,
                  newer_id: res.uuid,
                })
              : null,
          ]);
          router.back();
        } catch (err: any) {
          console.log("err: ", err);
          if (
            err?.response?.status === 422 &&
            err?.response?.data?.message?.message === "Identity conflict" &&
            setActiveStep
          ) {
            console.log();
            setErrorHandlingModalData([
              {
                index: primaryBidderIndex,
                ...err?.response?.data?.message?.errorDetails,
              },
            ]);
            setActiveStep(activeStep > 0 ? activeStep - 1 : 0);
            setIsErrorHandlingModalOpen({ open: true, type: "api" });
            return;
          }
          if (
            err?.response?.status === 422 &&
            err?.response?.data?.message ===
              "Identity conflict(s) in joint bidders" &&
            setActiveStep
          ) {
            console.log(
              "err?.response?.data?.errors: ",
              err?.response?.data?.errors,
            );
            const data = err?.response?.data?.errors.filter(
              (detail: any) => detail?.errorDetails,
            );
            console.log("data: ", data);
            const errorData = [];
            for (let i = 0; i < data?.length; i++) {
              errorData.push({
                email: data[i].errorDetails.email,
                mobile_number: data[i].errorDetails.mobile,
              });
            }

            setErrorHandlingModalData(errorData || []);
            setActiveStep(activeStep > 0 ? activeStep - 1 : 0);
            setIsErrorHandlingModalOpen({ open: true, type: "api" });
            return;
          }
          if (err?.response?.status === 422 && setActiveStep) {
            const errorData = [];
            const existingEmailsArray =
              err?.response?.data?.errorDetails?.existingEmails ?? [];
            const existingEmails =
              Array.isArray(existingEmailsArray) &&
              existingEmailsArray.length > 0
                ? existingEmailsArray.join(", ")
                : "";

            errorData.push({
              email: err?.response?.data?.errors?.email?.[0] ?? "",
              email_list: existingEmails,
            });

            setErrorHandlingModalData(errorData || []);
            setActiveStep(activeStep > 0 ? activeStep - 1 : 0);
            setIsErrorHandlingModalOpen({ open: true, type: "validation" });
            return;
          }
          const errorData = [];
          errorData.push({
            message: "Failed to create record. Please try again later.",
          });
          setErrorHandlingModalData(errorData);
          if (setActiveStep) {
            setActiveStep(activeStep > 0 ? activeStep - 1 : 0);
          }
          setIsErrorHandlingModalOpen({ open: true, type: "error" });
        }
      } else {
        console.error(
          "form validation failed for bidders--->>>",
          allBiddersData,
        );
      }
    } catch (error) {
      console.error("Error in submitting all bidders:", error);
    }
  };
  const focusFieldById = (id: any) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.focus({ preventScroll: true });
  };

  const handleSubmit = async () => {
    try {
      form.value.email = form.value.email.trim().toLowerCase();
      if (form.validate()) {
        form.startProcessing();
        if (mode === "edit") {
          try {
            let payload = form.value;
            payload["uuid"] = id;
            if (
              payload.kyc_verified &&
              payload.emd_amount_paid &&
              payload.agreement_signed &&
              payload.application_form_filled
            ) {
              payload["status"] = "verified";
            } else {
              payload["status"] = "pending";
            }

            const eventData: any = await getAuctionEventById(for_event);
            console.log("event::1234::>", eventData, for_event);
            payload["meta"] = {
              banker_email: banker?.attributes[0]?.value,
              bank_name: bank?.name,
              user_details: {
                address: payload["address"],
                mobile_number: payload["mobile_number"],
                aadhar_number: payload["aadhar_number"],
                user_type: payload["user_type"],
              },
              approver_email:
                eventData?.meta?.approver &&
                eventData?.meta?.approver.length > 0
                  ? eventData?.meta?.approver[0]?.email
                  : "",
              approver_name:
                eventData?.meta?.approver &&
                eventData?.meta?.approver.length > 0
                  ? eventData?.meta?.approver[0]?.name
                    ? eventData?.meta?.approver[0]?.name
                    : eventData?.meta?.approver[0]?.email
                  : "",
              branch_name: currentBranch?.label,
              event_name: eventData?.event_name,
              dashboard_url: `${process.env.NEXT_PUBLIC_BIDDER_DB_URL}/auction-events`,
              event_id: eventData.listing_id,
              auction_start_datetime: DateTime.fromISO(
                eventData?.start_date_time,
              )
                .setZone("Asia/Kolkata")
                .toFormat("dd MMMM yyyy hh:mm a"),
              emd_last_datetime: DateTime.fromISO(
                eventData?.property_dump?.emd_last_date,
              )
                .setZone("Asia/Kolkata")
                .toFormat("dd MMMM yyyy hh:mm a"),
              name: payload.name,
              pan: payload.pan,
            };
            payload["digio_request_status"] = "approved";
            const res = await updateBidderForEvent(payload);
            if (res) {
              router.back();
            }
            return true;
          } catch (error: any) {
            console.log("error::", error);
            if (error?.response?.data?.errors) {
              form.setErrors(error.response.data.errors);
            }

            const errorDetails =
              error?.response?.data?.message?.errorDetails ||
              error?.response?.data?.errorDetails;

            if (errorDetails) {
              let emailError = errorDetails?.email;
              let mobileError = errorDetails?.mobile;

              if (emailError && mobileError)
                form.setErrors({
                  email: [emailError],
                  mobile_number: [mobileError],
                });
              else if (emailError)
                form.setErrors({
                  email: [emailError],
                });
              else if (mobileError)
                form.setErrors({
                  mobile_number: [mobileError],
                });
            }

            return false;
          }
        } else {
          try {
            let payload = form?.value;

            if (
              payload.kyc_verified &&
              payload.emd_amount_paid &&
              payload.agreement_signed &&
              payload.application_form_filled
            ) {
              payload["status"] = "verified";
            } else {
              payload["status"] = "pending";
            }

            const eventData: any = await getAuctionEventById(for_event);

            payload["meta"] = {
              banker_email: banker?.attributes[0]?.value,
              bank_name: bank?.name,
              user_details: {
                address: payload["address"],
                mobile_number: payload["mobile_number"],
                aadhar_number: payload["aadhar_number"],
                user_type: payload["user_type"],
              },
              approver_email:
                eventData?.meta?.approver &&
                eventData?.meta?.approver.length > 0
                  ? eventData?.meta?.approver[0]?.email
                  : "",
              approver_name:
                eventData?.meta?.approver &&
                eventData?.meta?.approver.length > 0
                  ? eventData?.meta?.approver[0]?.name
                    ? eventData?.meta?.approver[0]?.name
                    : eventData?.meta?.approver[0]?.email
                  : "",
              branch_name: currentBranch?.label,
              event_name: eventData?.event_name,
              dashboard_url: `${process.env.NEXT_PUBLIC_BIDDER_DB_URL}/auction-events`,
              event_id: eventData.listing_id,
              auction_start_datetime: DateTime.fromISO(
                eventData?.start_date_time,
              )
                .setZone("Asia/Kolkata")
                .toFormat("dd MMMM yyyy hh:mm a"),
              emd_last_datetime: DateTime.fromISO(
                eventData?.property_dump?.emd_last_date,
              )
                .setZone("Asia/Kolkata")
                .toFormat("dd MMMM yyyy hh:mm a"),
              name: payload.name,
              pan: payload.pan,
            };
            payload["digio_request_status"] = "approved";
            payload["event_uuid"] = eventData?.uuid;
            if (isOverwrite) {
              payload["is_overridden"] = true;
            }

            const res: any = await addBidderForEvent(payload);
            if (res) {
              console.log("payload response", res);
              let uuid = res?.uuid;

              if (!uuid || kycFiles?.length === 0) {
                router.back();
              }
              const payload = {
                older_id: randomId,
                newer_id: uuid,
              };
              const idChangeRes = await bulkFileOwnerServicechange(payload);
              if (idChangeRes) router.back();
            }
            return true;
          } catch (error: any) {
            console.log("error", error);
            if (error?.response?.data?.errors) {
              form.setErrors(error.response.data.errors);
            }

            const errorDetails =
              error?.response?.data?.message?.errorDetails ||
              error?.response?.data?.errorDetails;

            if (errorDetails) {
              let emailError = errorDetails?.email;
              let mobileError = errorDetails?.mobile;

              if (emailError && mobileError) {
                form.setErrors({
                  email: [emailError],
                  mobile_number: [mobileError],
                });
                focusFieldById("name");
                setErrorHandlingModalData([
                  {
                    email: emailError,
                    mobile_number: mobileError,
                  },
                ]);
                setIsErrorHandlingModalOpen({ open: true, type: "api" });
              } else if (emailError) {
                form.setErrors({
                  email: [emailError],
                });
                focusFieldById("name");
                setErrorHandlingModalData([
                  {
                    email: emailError,
                  },
                ]);
                setIsErrorHandlingModalOpen({ open: true, type: "api" });
              } else if (mobileError) {
                form.setErrors({
                  mobile_number: [mobileError],
                });
                focusFieldById("mobile_number");
                setErrorHandlingModalData([
                  {
                    mobile_number: mobileError,
                  },
                ]);
                setIsErrorHandlingModalOpen({ open: true, type: "api" });
              }
            }
            return false;
          }
        }
      }
    } catch (error: any) {
      form.finishProcessing();
      console.log("check here error", error);
      if (error?.response?.data?.errors) {
        form.setErrors(error.response.data.errors);
      }
    }
  };

  const handlePrimaryBidderChange = (
    checked: boolean,
    index: number | undefined,
  ) => {
    if (setPrimaryBidderIndex && index !== undefined) {
      if (checked) setPrimaryBidderIndex(index);
      else setPrimaryBidderIndex(-1);
    }
  };
  const handleAddBidderAnyway = async () => {
    try {
      if (mode === "joint-creation") {
        await handleSubmitAllBidders();
      } else {
        await handleSubmit();
      }
    } catch (error) {
      console.log("error:check here---->>>", error);
    }
  };
  return (
    <>
      {activeStep + 1 === totalBidders ? (
        <div
          className={` ${
            bidderFormWrapperClassName ??
            "lg:pl-9 lg:py-[41px] lg:pr-[70px] space-y-8 lg:space-y-7 lg:mr-[88px] mt-9 lg:mt-6 lg:bg-brand-bgLightGrey"
          }`}
        >
          {/* Doc Upload Component */}
          <div className="translate-y-2">
            <p className="font-openSans font-normal mb-2 text-mobile-h3 lg:text-web-subheading leading-8 lg:leading-6 lg:ml-5 text-bankerColor">
              Joint Declaration Form Upload
            </p>
            <div
              className={` ${
                mode === "joint-creation" || bidderData?.created_by === "banker"
                  ? ""
                  : ""
              }`}
            >
              <CustomMultipleFileUploader
                key={renderKey}
                handleChange={setJointDeclarationFiles}
                allowedMimeTypes={["application/pdf", "image/png"]}
                property_uuid={
                  mode === "joint-creation" && typeof id === "string"
                    ? id
                    : jointDeclarationRandomId
                }
                owner_service_type="joint-declaration-documents"
                data={jointDeclarationFiles}
              />
            </div>
          </div>

          {/* PDF Viewer */}
          {mode === "joint-bidder-view" && (
            <div className="translate-y-2">
              <p className="font-openSans font-normal mb-2 text-mobile-h3 lg:text-web-subheading leading-8 lg:leading-6 lg:ml-5 text-bankerColor">
                View Joint Declaration Form
              </p>
              <CustomMultiplePDFViewer
                key={renderKey}
                handleChange={setKycFiles}
                allowedMimeTypes={["application/pdf"]}
                property_uuid={typeof id === "string" ? id : randomId}
                owner_service_type="kyc-documents"
                data={kycFiles}
              />
            </div>
          )}

          {/* Doc Upload Component */}
          <div className="translate-y-2">
            <p className="font-openSans font-normal mb-2 text-mobile-h3 lg:text-web-subheading leading-8 lg:leading-6 lg:ml-5 text-bankerColor">
              Upload KYC documents
            </p>
            <div
              className={` ${
                mode === "create" ||
                mode === "joint-creation" ||
                bidderData?.created_by === "banker"
                  ? ""
                  : "pointer-events-none opacity-50 select-none"
              }`}
            >
              <CustomMultipleFileUploader
                key={renderKey}
                handleChange={setKycFiles}
                allowedMimeTypes={["application/pdf", "image/png"]}
                property_uuid={
                  mode === "edit" && typeof id === "string" ? id : randomId
                }
                owner_service_type="kyc-documents"
                data={kycFiles}
              />
            </div>
          </div>

          {/* PDF Viewer */}
          {mode === "joint-bidder-view" && (
            <div className="translate-y-2">
              <p className="font-openSans font-normal mb-2 text-mobile-h3 lg:text-web-subheading leading-8 lg:leading-6 lg:ml-5 text-bankerColor">
                View KYC documents
              </p>
              <CustomMultiplePDFViewer
                key={renderKey}
                handleChange={setKycFiles}
                allowedMimeTypes={["application/pdf"]}
                property_uuid={typeof id === "string" ? id : randomId}
                owner_service_type="kyc-documents"
                data={kycFiles}
              />
            </div>
          )}

          <div className="flex justify-end space-x-4">
            <Button onClick={handlePreviousStep}>Previous</Button>
            {activeStep !== undefined &&
            totalBidders &&
            activeStep === totalBidders - 1 ? (
              <Button onClick={handleSubmitAllBidders}>Submit All</Button>
            ) : (
              <Button
                onClick={() => {
                  handleValidationForJointParticipation();
                }}
              >
                Next
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div
          className={` ${
            bidderFormWrapperClassName ??
            "lg:pl-9 lg:py-[41px] lg:pr-[70px] space-y-8 lg:space-y-7 lg:mr-[88px] mt-9 lg:mt-6 lg:bg-brand-bgLightGrey"
          }`}
        >
          {bidderData?.created_by !== "user" && (
            <div className={`relative w-full`}>
              <Dropdown
                disabled={
                  mode === "view" ||
                  mode === "edit" ||
                  mode === "joint-creation"
                }
                dataSource={async () => {
                  return [
                    { label: "Individual", value: "individual" },
                    { label: "Proprietors", value: "proprietors" },
                    { label: "Partnership", value: "partnership" },
                    { label: "Company", value: "company" },
                  ];
                }}
                defaultSelected={
                  form.getField("user_type") !== ""
                    ? form.getField("user_type")
                    : []
                }
                selectIdentifier={true}
                onChange={(value: any) => {
                  if (mode === "create") {
                    form.setField("pan", "");
                    form.setField("name", "");
                    setIsNameVerified(false);
                  }
                  form.setField("user_type", value);
                }}
                valueKey="value"
                styles={{
                  dropdownItemsClass: "bg-white text-brand-borderGrey",
                  hoverBgColor: "focus:bg-brand-lightOrange",
                  hoverTextColor: "focus:text-brand-borderGrey",
                  hideDot: true,
                  outerContainer: `border-[1px] min-w-[130px] bg-white   w-full rounded-none h-[50px] font-openSans font-normal text-web-body-sm  mid:text-web-body-md ${
                    form.getField("user_type")
                      ? "items-end text-brand-textLightGrey"
                      : "text-brand-borderGrey"
                  } leading-5 `,
                }}
              />
              <label
                htmlFor="name"
                className={`absolute font-openSans font-normal  left-3 transition-all duration-300 -translate-y-1/2  text-gray-400 top-1/4 text-[12px] leading-[12px] peer-focus:text-brand-orange`}
              >
                Type of User
              </label>
              {form.errors.get("user_type") && (
                <p className="text-brand-textRed  mt-1 text-web-body-sm ml-2">
                  {form.errors.get("user_type")[0]}
                </p>
              )}
            </div>
          )}

          {bidderData?.created_by !== "user" && (
            <div className={`relative w-full`}>
              <Input
                id="name"
                disabled={mode === "view" || mode === "edit"}
                value={form.getField("email")}
                onChange={(e) => form.setField("email", e.target.value)}
                className={`bg-white  peer block w-full appearance-none border pt-3 pb-0 pl-4 h-[50px] focus:pb-0 outline-none font-openSans font-normal text-web-body-sm lg:text-web-body-md leading-3 lg:leading-4 text-brand-textLightGrey focus:border-brand-orange`}
              />
              <label
                htmlFor="name"
                className={`absolute font-openSans font-normal  left-4 transition-all duration-300 -translate-y-1/2  text-gray-400 top-1/4 text-[12px] leading-[13px] peer-focus:text-brand-orange`}
              >
                Email
              </label>
              {form.errors.get("email") && (
                <p className="text-brand-textRed  mt-1 text-web-body-sm ml-2">
                  {form.errors.get("email")[0]}
                </p>
              )}
            </div>
          )}
          {bidderData?.created_by !== "user" && (
            <div className={`relative w-full`}>
              <Input
                id="name"
                disabled={mode === "view"}
                // disabled={mode === "view"}
                value={form.getField("name")}
                onChange={(e) =>
                  form.setField("name", e.target.value?.toUpperCase())
                }
                className={`bg-white capitalize peer block w-full appearance-none border pt-3 pb-0 pl-4 h-[50px] focus:pb-0 outline-none font-openSans font-normal text-web-body-sm lg:text-web-body-md leading-3 lg:leading-4 text-brand-textLightGrey focus:border-brand-orange`}
              />
              <label
                htmlFor="name"
                className={`absolute font-openSans font-normal  left-4 transition-all duration-300 -translate-y-1/2  text-gray-400 top-1/4 text-[12px] leading-[13px] peer-focus:text-brand-orange`}
              >
                Name
              </label>
              {form.errors.get("name") && (
                <p className="text-brand-textRed  mt-1 text-web-body-sm ml-2">
                  {form.errors.get("name")[0]}
                </p>
              )}
            </div>
          )}
          {bidderData?.created_by !== "user" && (
            <div className={`relative w-full`}>
              {isNameVerified && (
                <div className="absolute top-1/3 right-2">
                  <CheckIcon className="h-5 w-5" />
                </div>
              )}
              <Input
                id="pan"
                disabled={
                  mode === "view" ||
                  mode === "edit" ||
                  (isDigioPanFullVerificationEnabled &&
                    (!form.getField("name") ||
                      form.getField("name").length < 3))
                }
                value={form.getField("pan")}
                onChange={async (e: any) => {
                  if (e?.target?.value?.length < 11) {
                    let value = e?.target?.value.toUpperCase();
                    form.setField("pan", value);
                    if (panRegex.test(value)) {
                      try {
                        setIsFetchingPan(true);
                        console.log("called");

                        const payload: any = {
                          card_type: "PAN",
                          card_number: value,
                        };

                        // V4 mode: require name >= 3 chars before calling API
                        const userName = (form.getField("name") || "")
                          .toString()
                          .trim();
                        if (
                          isDigioPanFullVerificationEnabled &&
                          userName.length < 3
                        ) {
                          form.pushError({
                            pan: [
                              "Please enter name (minimum 3 characters) before PAN verification.",
                            ],
                          });
                          setIsFetchingPan(false);
                          return;
                        }

                        // Add name if full verification is enabled AND name has at least 3 chars
                        // Otherwise, don't send name to allow legacy behavior
                        if (
                          isDigioPanFullVerificationEnabled &&
                          userName.length >= 3
                        ) {
                          payload.name = userName.toUpperCase();
                        }

                        const res: any = await fetchIDCardDetails(payload);
                        setIsFetchingPan(false);
                        await handleVerifyPan(res);
                      } catch (error: any) {
                        console.log("error:here ", error);

                        // Handle 422 errors with Digio error messages
                        if (error?.response?.status === 422) {
                          const errorMessage =
                            error?.response?.data?.message?.message ||
                            "Validation failed. Please check your details.";
                          form.pushError({
                            pan: [errorMessage],
                          });
                        } else {
                          form.pushError({
                            pan: [
                              "An internal error occurred. Please try again later.",
                            ],
                          });
                        }
                        // Legacy mode: clear name from API error
                        // V4 mode: keep user's entered name
                        if (!isDigioPanFullVerificationEnabled) {
                          form.setField("name", "");
                        }
                        setIsFetchingPan(false);
                        setIsNameVerified(false);
                      }
                    } else {
                      form.setField("name", "");
                      setIsFetchingPan(false);
                      setIsNameVerified(false);
                    }
                  }
                }}
                className={`bg-white peer block w-full appearance-none border pt-3 pb-0 pl-4 h-[50px] focus:pb-0 outline-none font-openSans font-normal text-web-body-sm lg:text-web-body-md leading-3 lg:leading-4 text-brand-textLightGrey focus:border-brand-orange`}
              />
              <label
                htmlFor="name"
                className={`absolute font-openSans font-normal  left-4 transition-all duration-300 -translate-y-1/2  text-gray-400 top-1/4 text-[12px] leading-[13px] peer-focus:text-brand-orange`}
              >
                Pan no.
              </label>
              {form.errors.get("pan") && (
                <p className="text-brand-textRed  mt-1 text-web-body-sm ml-2">
                  {form.errors.get("pan")[0]}
                </p>
              )}
            </div>
          )}
          {bidderData?.created_by !== "user" && (
            <div className={`relative w-full`}>
              <Input
                id="aadhar_number"
                disabled={mode === "view"}
                value={form.getField("aadhar_number")}
                onChange={(e) => form.setField("aadhar_number", e.target.value)}
                className={`bg-white  peer block w-full appearance-none border pt-3 pb-0 pl-4 h-[50px] focus:pb-0 outline-none font-openSans font-normal text-web-body-sm lg:text-web-body-md leading-3 lg:leading-4 text-brand-textLightGrey focus:border-brand-orange`}
              />
              <label
                htmlFor="aadhar_number"
                className={`absolute font-openSans font-normal  left-4 transition-all duration-300 -translate-y-1/2  text-gray-400 top-1/4 text-[12px] leading-[13px] peer-focus:text-brand-orange`}
              >
                Aadhar no.
              </label>
              {form.errors.get("aadhar_number") && (
                <p className="text-brand-textRed  mt-1 text-web-body-sm ml-2">
                  {form.errors.get("aadhar_number")[0]}
                </p>
              )}
            </div>
          )}
          {bidderData?.created_by !== "user" && (
            <div className={`relative w-full`}>
              <Input
                id="mobile_number"
                disabled={mode === "view" || mode === "edit"}
                value={form.getField("mobile_number")}
                onChange={(e) => form.setField("mobile_number", e.target.value)}
                className={`bg-white peer block w-full appearance-none border pt-3 pb-0 pl-4 h-[50px] focus:pb-0 outline-none font-openSans font-normal text-web-body-sm lg:text-web-body-md leading-3 lg:leading-4 text-brand-textLightGrey focus:border-brand-orange`}
              />
              <label
                htmlFor="mobile_number"
                className={`absolute font-openSans font-normal  left-4 transition-all duration-300 -translate-y-1/2  text-gray-400 top-1/4 text-[12px] leading-[13px] peer-focus:text-brand-orange`}
              >
                Mobile no.
              </label>
              {form.errors.get("mobile_number") && (
                <p className="text-brand-textRed  mt-1 text-web-body-sm ml-2">
                  {form.errors.get("mobile_number")[0]}
                </p>
              )}
            </div>
          )}
          {bidderData?.created_by !== "user" && (
            <div className={`relative w-full`}>
              <Input
                id="address"
                disabled={mode === "view"}
                value={form.getField("address")}
                onChange={(e) => form.setField("address", e.target.value)}
                className={`bg-white  peer block w-full appearance-none border pt-3 pb-0 pl-4 h-[50px] focus:pb-0 outline-none font-openSans font-normal text-web-body-sm lg:text-web-body-md leading-3 lg:leading-4 text-brand-textLightGrey focus:border-brand-orange`}
              />
              <label
                htmlFor="address"
                className={`absolute font-openSans font-normal  left-4 transition-all duration-300 -translate-y-1/2  text-gray-400 top-1/4 text-[12px] leading-[13px] peer-focus:text-brand-orange`}
              >
                Address
              </label>
              {form.errors.get("address") && (
                <p className="text-brand-textRed  mt-1 text-web-body-sm ml-2">
                  {form.errors.get("address")[0]}
                </p>
              )}
            </div>
          )}
          {bidderData?.created_by !== "user" && (
            <div className="flex flex-col lg:grid lg:grid-cols-2 gap-y-6 mt-3 lg:mt-0  lg:gap-y-5 ">
              {switchFields.map((item: any, index) => (
                <div
                  key={index}
                  className="flex gap-x-4 items-center ml-4 lg:ml-0"
                >
                  <Checkbox
                    checkIconClassName="text-brand-bgBlack"
                    className="font-openSans font-normal text-web-body-sm leading-4 data-[state=checked]:bg-[#EEEEEE] border-brand-bgBlack lg:hidden"
                    id={`asset-${index}`}
                    checked={
                      form.getField(item?.value) !== ""
                        ? form.getField(item?.value)
                        : false
                    }
                    onCheckedChange={(checked: boolean) => {
                      handleSwitchChange(checked, item?.value);
                    }}
                  />
                  <Switch
                    disabled={mode === "view"}
                    thumbClassName="h-6 w-6 data-[state=unchecked]:bg-brand-textGrey"
                    checked={
                      form.getField(item?.value) !== ""
                        ? form.getField(item?.value)
                        : false
                    }
                    onCheckedChange={(checked) => {
                      handleSwitchChange(checked, item?.value);
                    }}
                    className="data-[state=checked]:bg-orange-500 data-[state=unchecked]:bg-transparent hidden lg:flex  data-[state=unchecked]:border-2 data-[state=unchecked]:border-brand-textGrey p-1 h-8 w-[52px] data-[state=unchecked]:text-brand-textGrey"
                  />
                  <p className="font-openSans font-normal text-web-body-sm leading-[14px] text-brand-textGrey">
                    {item?.title}
                  </p>
                </div>
              ))}
            </div>
          )}

          {mode === "joint-creation" && (
            <div className=" gap-x-4 items-center ml-4 lg:ml-0 hidden">
              <Checkbox
                checkIconClassName="text-brand-bgBlack"
                className="font-openSans font-normal text-web-body-sm leading-4 data-[state=checked]:bg-[#EEEEEE] border-brand-bgBlack lg:hidden"
                checked={activeStep === primaryBidderIndex}
                onCheckedChange={(checked: boolean) => {
                  handlePrimaryBidderChange(checked, activeStep);
                }}
              />
              <Switch
                // disabled={mode === "view"}
                thumbClassName="h-6 w-6 data-[state=unchecked]:bg-brand-textGrey"
                checked={activeStep === primaryBidderIndex}
                onCheckedChange={(checked: boolean) => {
                  handlePrimaryBidderChange(checked, activeStep);
                }}
                className="data-[state=checked]:bg-orange-500 data-[state=unchecked]:bg-transparent hidden lg:flex  data-[state=unchecked]:border-2 data-[state=unchecked]:border-brand-textGrey p-1 h-8 w-[52px] data-[state=unchecked]:text-brand-textGrey"
              />
              <p className="font-openSans font-normal text-web-body-sm leading-[14px] text-brand-textGrey">
                Primary Bidder
              </p>
            </div>
          )}

          {/* Doc Upload Component */}
          {mode === "create" || mode === "edit" ? (
            <div className="translate-y-2">
              <p className="font-openSans font-normal mb-2 text-mobile-h3 lg:text-web-subheading leading-8 lg:leading-6 lg:ml-5 text-bankerColor">
                Upload KYC documents
              </p>
              <div
                className={` ${
                  mode === "create" || bidderData?.created_by === "banker"
                    ? ""
                    : "pointer-events-none opacity-50 select-none"
                }`}
              >
                <CustomMultipleFileUploader
                  key={renderKey}
                  handleChange={setKycFiles}
                  allowedMimeTypes={["application/pdf", "image/png"]}
                  property_uuid={
                    mode === "edit" && typeof id === "string" ? id : randomId
                  }
                  owner_service_type="kyc-documents"
                  data={kycFiles}
                />
              </div>
            </div>
          ) : (
            <></>
          )}
          {/* PDF Viewer */}
          {mode === "view" && (
            <div className="translate-y-2">
              <p className="font-openSans font-normal mb-2 text-mobile-h3 lg:text-web-subheading leading-8 lg:leading-6 lg:ml-5 text-bankerColor">
                View KYC documents
              </p>
              <CustomMultiplePDFViewer
                key={renderKey}
                handleChange={setKycFiles}
                allowedMimeTypes={["application/pdf"]}
                property_uuid={typeof id === "string" ? id : randomId}
                owner_service_type="kyc-documents"
                data={kycFiles}
              />
            </div>
          )}
          {mode === "view" &&
            bidderData?.access_type === "primary" &&
            bidderData?.is_joint && (
              <div className="translate-y-2">
                <p className="font-openSans font-normal mb-2 text-mobile-h3 lg:text-web-subheading leading-8 lg:leading-6 lg:ml-5 text-bankerColor">
                  View Joint Declaration Files
                </p>
                <CustomMultiplePDFViewer
                  key={renderKey}
                  handleChange={setJointDeclarationFiles}
                  allowedMimeTypes={["application/pdf"]}
                  property_uuid={typeof id === "string" ? id : randomId}
                  owner_service_type="joint-declaration-documents"
                  data={jointDeclarationFiles}
                />
              </div>
            )}
          {/* Submit Functionality */}
          {mode === "view" ? (
            <></>
          ) : mode === "joint-creation" ? (
            <div className="flex justify-end space-x-4">
              <Button onClick={handlePreviousStep}>Previous</Button>
              {activeStep !== undefined &&
              totalBidders &&
              activeStep === totalBidders - 1 ? (
                <Button onClick={handleSubmitAllBidders}>Submit All</Button>
              ) : (
                <Button
                  onClick={() => {
                    handleValidationForJointParticipation();
                  }}
                >
                  Next
                </Button>
              )}
            </div>
          ) : (
            <div className="w-full ">
              <Button
                onClick={handleSubmit}
                className="flex ml-auto w-full lg:w-fit mt-16 lg:mt-4"
              >
                {mode === "edit" ? "Update" : "Add Bidder"}
              </Button>
            </div>
          )}
          {/* Accept Reject Functionality */}
          {mode === "view" && bidderData?.created_by !== "banker" && (
            <div className="w-full flex flex-col md:flex-row justify-center md:justify-end  mt-10">
              <div className="space-x-3 md:space-x-3">
                <Button
                  disabled={
                    isLoading ||
                    bidderData?.status === "verified" ||
                    bidderData?.status === "rejected"
                  }
                  onClick={handleAcceptEvent}
                  className="px-5 py-3 w-[140px] md:w-[125px]"
                >
                  Accept
                </Button>
                {/* <Button
                disabled={
                  isLoading ||
                  bidderData?.status === "verified" ||
                  bidderData?.status === "rejected"
                }
                onClick={() => {
                  setModalType("reattempt");
                  setShowRejectionModal(true);
                }}
                className="bg-[#fafafabb] border text-[#B60100] px-5 py-3 w-[140px] md:w-[165px] hover:bg-brand-borderLightGray"
              >
                Request Step Retry
              </Button> */}
                <Button
                  disabled={
                    isLoading ||
                    bidderData?.status === "verified" ||
                    bidderData?.status === "rejected"
                  }
                  onClick={() => {
                    // setModalType("rejection");
                    setShowRejectionModal(true);
                  }}
                  className="bg-[#fafafabb] border text-[#B60100] px-5 py-3 w-[140px] md:w-[125px] hover:bg-brand-borderLightGray"
                >
                  Reject
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {showRejectionModal && (
        <CustomRejectionModal
          handleSubmit={handleRejectEvent}
          modalType={"rejection"}
          onClose={setShowRejectionModal}
          isLoading={isLoading}
        />
      )}
      {process.env.JBP_ENABLE === "true" && isErrorHandlingModalOpen?.open && (
        <CustomErrorHandlingModal
          errorType={isErrorHandlingModalOpen?.type || "validation"}
          onClose={setIsErrorHandlingModalOpen}
          errorValue={errorHandlingModalData}
          setIsErrorHandlingModalOpen={setIsErrorHandlingModalOpen}
          setErrorHandlingModalData={setErrorHandlingModalData}
          errorHandlingModalData={errorHandlingModalData}
          handleAddBidderAnyway={handleAddBidderAnyway}
          setIsOverwrite={setIsOverwrite}
        />
      )}
    </>
  );
};

export default BidderForm;
